"use client";

import { Share, Download, ChevronDown, Copy, MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useLanguageContext } from "@/contexts/language-context";
import { useToast } from "@/hooks/use-toast";
import { usePWA } from "@/contexts/pwa-context";

export function InstallPrompt() {
  const { getTranslation } = useLanguageContext();
  const { toast } = useToast();
  const { 
    showIOSInstructions, 
    showAndroidChecking,
    showAndroidInstructions,
    showAndroidPostInstallHelp,
    showGenericInstructions,
    showInAppWarning, 
    showPageLoadWaiting,
    isIOS,
    dismissInstallPrompt,
    installChannel,
    install
  } = usePWA();

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast({
        title: getTranslation("pwa.linkCopied"),
        description: getTranslation("pwa.copyLink"),
      });
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  if (
    !showIOSInstructions &&
    !showAndroidChecking &&
    !showInAppWarning &&
    !showPageLoadWaiting &&
    !showAndroidInstructions &&
    !showAndroidPostInstallHelp &&
    !showGenericInstructions
  ) {
    return null;
  }

  // --- Render Page Load Waiting ---
  if (showPageLoadWaiting) {
    return (
      <div
        className={cn(
          "fixed z-50",
          "bottom-20 left-4 right-4",
          "md:bottom-4 md:left-auto md:right-4 md:w-96",
          "animate-in slide-in-from-bottom-5 fade-in duration-300"
        )}
      >
        <div className="bg-background/95 backdrop-blur-sm border border-border shadow-lg rounded-xl p-4 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-light-primary to-light-accent dark:from-dark-primary dark:to-dark-accent" />

          <button
            onClick={dismissInstallPrompt}
            className="absolute top-2 right-2 text-muted-foreground hover:text-foreground p-1 transition-colors"
          >
            <ChevronDown size={16} />
          </button>

          <div className="flex gap-4 items-start pt-2">
            <div className="bg-light-primary/10 dark:bg-dark-primary/10 p-3 rounded-lg text-light-primary dark:text-dark-primary shrink-0">
              <Download size={24} />
            </div>

            <div className="flex-1 space-y-2">
              <h3 className="font-semibold text-sm">
                {getTranslation("pwa.installTitle")}
              </h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {getTranslation("pwa.pageLoadWaitingDescription")}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- Render In-App Browser Warning ---
  if (showInAppWarning) {
    return (
      <div className={cn(
        "fixed z-50",
        "bottom-20 left-4 right-4", 
        "md:bottom-4 md:left-auto md:right-4 md:w-96",
        "animate-in slide-in-from-bottom-5 fade-in duration-300"
      )}>
        <div className="bg-background/95 backdrop-blur-sm border border-border shadow-lg rounded-xl p-4 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-light-primary to-light-accent dark:from-dark-primary dark:to-dark-accent" />
          
          <button
            onClick={dismissInstallPrompt}
            className="absolute top-2 right-2 text-muted-foreground hover:text-foreground p-1 transition-colors"
          >
            <ChevronDown size={16} />
          </button>

          <div className="flex gap-4 items-start pt-2">
            <div className="bg-amber-500/10 dark:bg-amber-500/20 p-3 rounded-lg text-amber-600 dark:text-amber-400 shrink-0">
               <MoreHorizontal size={24} />
            </div>
            
            <div className="flex-1 space-y-2">
              <h3 className="font-semibold text-sm">
                {getTranslation("pwa.installTitle")}
              </h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {getTranslation("pwa.inAppBrowserWarning")}
              </p>
              
              <div className="flex flex-col gap-2 mt-2">
                <div className="flex items-center gap-2 p-2 rounded bg-muted/50 text-xs text-muted-foreground">
                  <MoreHorizontal size={14} />
                  <span>{getTranslation("pwa.openInBrowserInstruction")}</span>
                </div>
                
                <Button 
                  onClick={handleCopyLink}
                  size="sm" 
                  className="w-full bg-gradient-to-r from-light-primary to-light-accent dark:from-dark-primary dark:to-dark-accent text-white border-0"
                >
                  <Copy className="mr-2 h-4 w-4" />
                  {getTranslation("pwa.copyLink")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- Render iOS Instructions ---
  if (showIOSInstructions && isIOS) {
    return (
        <div className={cn(
          "fixed z-50",
          "bottom-20 left-4 right-4", 
          "md:bottom-4 md:left-auto md:right-4 md:w-96",
          "animate-in slide-in-from-bottom-5 fade-in duration-300"
        )}>
          <div className="bg-background/95 backdrop-blur-sm border border-border shadow-lg rounded-xl p-4 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-light-primary to-light-accent dark:from-dark-primary dark:to-dark-accent" />
            
            <button
              onClick={dismissInstallPrompt}
              className="absolute top-2 right-2 text-muted-foreground hover:text-foreground p-1 transition-colors"
            >
              <ChevronDown size={16} />
            </button>
    
            <div className="flex gap-4 items-start pt-2">
              <div className="bg-light-primary/10 dark:bg-dark-primary/10 p-3 rounded-lg text-light-primary dark:text-dark-primary shrink-0">
                <Share size={24} />
              </div>
              
              <div className="flex-1 space-y-1">
                <h3 className="font-semibold text-sm">
                  {getTranslation("pwa.iosInstallTitle")}
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {getTranslation("pwa.iosInstallDescription")}
                </p>
              </div>
            </div>
          </div>
        </div>
      );
  }

  // --- Render Android Checking State ---
  if (showAndroidChecking) {
    return (
      <div
        className={cn(
          "fixed z-50",
          "bottom-20 left-4 right-4",
          "md:bottom-4 md:left-auto md:right-4 md:w-96",
          "animate-in slide-in-from-bottom-5 fade-in duration-300"
        )}
      >
        <div className="bg-background/95 backdrop-blur-sm border border-border shadow-lg rounded-xl p-4 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-light-primary to-light-accent dark:from-dark-primary dark:to-dark-accent" />

          <button
            onClick={dismissInstallPrompt}
            className="absolute top-2 right-2 text-muted-foreground hover:text-foreground p-1 transition-colors"
          >
            <ChevronDown size={16} />
          </button>

          <div className="flex gap-4 items-start pt-2">
            <div className="bg-light-primary/10 dark:bg-dark-primary/10 p-3 rounded-lg text-light-primary dark:text-dark-primary shrink-0">
              <Download size={24} />
            </div>

            <div className="flex-1 space-y-2">
              <h3 className="font-semibold text-sm">
                {getTranslation("pwa.installTitle")}
              </h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {getTranslation("pwa.androidInstallPendingDescription")}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showAndroidPostInstallHelp) {
    return (
      <div
        className={cn(
          "fixed z-50",
          "bottom-20 left-4 right-4",
          "md:bottom-4 md:left-auto md:right-4 md:w-96",
          "animate-in slide-in-from-bottom-5 fade-in duration-300"
        )}
      >
        <div className="bg-background/95 backdrop-blur-sm border border-border shadow-lg rounded-xl p-4 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 to-orange-500" />

          <button
            onClick={dismissInstallPrompt}
            className="absolute top-2 right-2 text-muted-foreground hover:text-foreground p-1 transition-colors"
          >
            <ChevronDown size={16} />
          </button>

          <div className="flex gap-4 items-start pt-2">
            <div className="bg-amber-500/10 p-3 rounded-lg text-amber-600 dark:text-amber-400 shrink-0">
              <Download size={24} />
            </div>

            <div className="flex-1 space-y-2">
              <h3 className="font-semibold text-sm">
                {getTranslation("pwa.installTitle")}
              </h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {getTranslation("pwa.androidInstallFailedDescription")}
              </p>

              <div className="text-xs text-muted-foreground bg-muted/50 rounded p-2">
                {getTranslation("pwa.androidInstallRetryInstruction")}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- Render Android Manual Instructions ---
  if (showAndroidInstructions) {
    const canOpenNativePrompt = installChannel === "native-prompt";

    return (
      <div
        className={cn(
          "fixed z-50",
          "bottom-20 left-4 right-4",
          "md:bottom-4 md:left-auto md:right-4 md:w-96",
          "animate-in slide-in-from-bottom-5 fade-in duration-300"
        )}
      >
        <div className="bg-background/95 backdrop-blur-sm border border-border shadow-lg rounded-xl p-4 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-light-primary to-light-accent dark:from-dark-primary dark:to-dark-accent" />

          <button
            onClick={dismissInstallPrompt}
            className="absolute top-2 right-2 text-muted-foreground hover:text-foreground p-1 transition-colors"
          >
            <ChevronDown size={16} />
          </button>

          <div className="flex gap-4 items-start pt-2">
            <div className="bg-light-primary/10 dark:bg-dark-primary/10 p-3 rounded-lg text-light-primary dark:text-dark-primary shrink-0">
              <Download size={24} />
            </div>

            <div className="flex-1 space-y-2">
              <h3 className="font-semibold text-sm">
                {getTranslation("pwa.installTitle")}
              </h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {canOpenNativePrompt
                  ? getTranslation("pwa.androidInstallReadyDescription")
                  : getTranslation("pwa.androidInstallDescription")}
              </p>

              {canOpenNativePrompt ? (
                <Button
                  onClick={install}
                  size="sm"
                  className="w-full bg-gradient-to-r from-light-primary to-light-accent dark:from-dark-primary dark:to-dark-accent text-white border-0"
                >
                  <Download className="mr-2 h-4 w-4" />
                  {getTranslation("pwa.installButton")}
                </Button>
              ) : (
                <div className="text-xs text-muted-foreground bg-muted/50 rounded p-2">
                  {getTranslation("pwa.androidInstallInstruction")}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- Render Generic Mobile Manual Instructions ---
  if (showGenericInstructions || installChannel === "generic-manual") {
    return (
      <div
        className={cn(
          "fixed z-50",
          "bottom-20 left-4 right-4",
          "md:bottom-4 md:left-auto md:right-4 md:w-96",
          "animate-in slide-in-from-bottom-5 fade-in duration-300"
        )}
      >
        <div className="bg-background/95 backdrop-blur-sm border border-border shadow-lg rounded-xl p-4 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-light-primary to-light-accent dark:from-dark-primary dark:to-dark-accent" />

          <button
            onClick={dismissInstallPrompt}
            className="absolute top-2 right-2 text-muted-foreground hover:text-foreground p-1 transition-colors"
          >
            <ChevronDown size={16} />
          </button>

          <div className="flex gap-4 items-start pt-2">
            <div className="bg-light-primary/10 dark:bg-dark-primary/10 p-3 rounded-lg text-light-primary dark:text-dark-primary shrink-0">
              <Download size={24} />
            </div>

            <div className="flex-1 space-y-2">
              <h3 className="font-semibold text-sm">
                {getTranslation("pwa.installTitle")}
              </h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {getTranslation("pwa.genericInstallDescription")}
              </p>

              <div className="text-xs text-muted-foreground bg-muted/50 rounded p-2">
                {getTranslation("pwa.genericInstallInstruction")}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
